import React, { useState, useEffect, useRef } from 'react';
import { useHistory, NavLink } from 'react-router-dom';
import { authService } from '@S';
import OtpInput from 'react-otp-input';
import axios from 'axios';
import { toast } from 'react-toastify';
import { authHeader } from '@H';
import SimpleReactValidator from 'simple-react-validator';

const Login = ({ history }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [otpGenerated, setOtpGenerated] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  useEffect(() => {
    if (authService.currentUserValue) {
      history.push('/dashboard');
    }
  }, [history]);

  const onChange = (field) => (event) => {
    const value = event.target.value;
    if (field === 'username') {
      setUsername(value);
    } else if (field === 'password') {
      setPassword(value);
    } else if (field === 'email') {
      setEmail(value);
    }
  };

  const forgotPassword = (e) => {
    e.preventDefault();
    setLoading(true);

    authService
      .forgotPassword(email)
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        setError(true);
        setLoading(false);
      });
  };

  const generateOTP = (e) => {
    e.preventDefault();
    setLoading(true);
    setOtpGenerated(true);
    setLoading(false);
  };

  const onTogglePasswd = () => {
    setShowPassword((prevState) => !prevState);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError(true);
      return;
    }
    setLoading(true);
    authService.login(username, password).then(
      (user) => {
        const token = authService.currentUserToken;
        authService.bunkDetails(token).then(() => {
          setLoading(false);
          history.push('/dashboard');
        });
      },
      (error) => {
        setError(true);
        setLoading(false);
      },
    );
  };

  const handleRegisterClick = () => {
    history.push('/registration');
  };

  return otpGenerated ? (
    otpVerified ? (
      <PasswordResetForm email={email} />
    ) : (
      <TwoStepAuth
        previousStep={() => setOtpGenerated(false)}
        email={email}
        onOtpVerified={() => setOtpVerified(true)}
      />
    )
  ) : (
    <div className="login-container">
      <div className="logo-wrapper" />
      <div className="content-wrapper">
        <h1>
          {forgotPasswordMode
            ? 'Forgot Password'
            : 'Sign in to manage your outlet'}
        </h1>

        <div className="form-wrapper">
          {forgotPasswordMode ? (
            // Forgot Password Form
            <form onSubmit={forgotPassword}>
              <div className="input-wrapper">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={onChange('email')}
                  className={error ? 'error' : ''}
                  required
                />
              </div>

              <div className="input-submit-wrapper">
                <button className="submit" onClick={(e) => generateOTP(e)}>
                  Continue
                </button>
              </div>

              <button
                className="forgot-password"
                onClick={() => setForgotPasswordMode(false)}
              >
                Back to Login
              </button>
            </form>
          ) : (
            // Login Form
            <form onSubmit={onSubmit}>
              <div className="input-wrapper">
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={onChange('username')}
                  className={error ? 'error' : ''}
                />
              </div>
              <div
                className={
                  showPassword ? 'input-wrapper visible' : 'input-wrapper'
                }
              >
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={onChange('password')}
                  className={error ? 'error' : ''}
                />
                <span className="show-psswd" onClick={onTogglePasswd} />
              </div>
              <div className="input-submit-wrapper">
                <button className={loading ? 'loading' : ''} type="submit">
                  Login
                </button>
              </div>

              <button
                className="forgot-password"
                onClick={() => setForgotPasswordMode(true)}
              >
                Forgot password
              </button>
            </form>
          )}
        </div>

        {!forgotPasswordMode && (
          <div className="register-wrapper">
            <div className="border-container">
              <div className="border-left"></div>
              <span>or</span>
              <div className="border-right"></div>
            </div>
            <NavLink to="/subscription" className="register-link">
              Register
            </NavLink>
          </div>
        )}
      </div>
    </div>
  );
};

const TwoStepAuth = ({ email, previousStep, onOtpVerified }) => {
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(60);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    generateOTP();
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev === 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const generateOTP = () => {
    setLoading(true);
    const from_app = 'FORGOT';
    const headers = authHeader(1);

    axios
      .post(
        `${process.env.API_URL}/two-step-auth/`,
        {
          from_app,
          email,
        },
        { headers },
      )
      .then((response) => {
        setLoading(false);
        setTimer(60);
      })
      .catch((error) => {
        setLoading(false);
        console.error('Error sending OTP:', error);
        toast.error('Error generating OTP');
      });
  };

  const verifyOTP = (e) => {
    e.preventDefault();
    if (!otp) {
      toast.error('Please enter OTP');
      return;
    }

    setLoading(true);
    setIsSubmitting(true);
    const headers = authHeader(1);

    axios
      .get(`${process.env.API_URL}/users/validate-user-otp/?otp=${otp}`, {
        headers,
      })
      .then((response) => {
        setLoading(false);
        toast.success(response.data.message);
        setIsSubmitting(false);
        onOtpVerified();
      })
      .catch((error) => {
        console.error('Error verifying OTP:', error);
        setIsSubmitting(false);
        toast.error(error.response.data.message);
      });
  };

  return (
    <div className="otp-container">
      <form className="otp-form">
        <div className="otp-header">
          <h4>Check your email</h4>
          <p>Please enter the six-digit verification code we sent to {email}</p>
        </div>
        <div className="input-wrapper otp-input">
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={6}
            renderSeparator={<span>-</span>}
            inputStyle={{
              width: '3rem',
              height: '3rem',
              margin: '0 0.5rem',
              fontSize: '2rem',
              borderRadius: '5px',
              border: '1px solid #ced4da',
            }}
            renderInput={(props) => <input {...props} />}
          />
        </div>
        <div className="cta-wrapper">
          <button
            className={`submit ${isSubmitting ? 'loading' : ''}`}
            onClick={(e) => verifyOTP(e)}
          >
            Confirm
          </button>
        </div>
        <div className="otp-footer">
          <span>Didn't receive the code? </span>
          {timer === 0 ? (
            <button className="resend-otp" onClick={() => generateOTP()}>
              Resend OTP
            </button>
          ) : (
            <span>Resend in {timer} sec</span>
          )}
          <p>
            Not your email address?{' '}
            <button className="link link-back" onClick={previousStep}>
              Go back
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

const PasswordResetForm = ({ email }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirm_password: '',
    is_password_udate: true,
  });
  const [loading, setLoading] = useState(false);
  const [, forceUpdate] = useState();
  const simpleValidator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } }),
  );

  const onTogglePasswd = () => {
    setShowPassword(!showPassword);
  };

  const onToggleConfirmPasswd = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }
    setLoading(true);

    const headers = authHeader(1);
    const payload = {
      email,
      password: formData.password,
      // confirm_password: formData.confirm_password,
      is_password_update: true,
    };

    axios
      .post(`${process.env.API_URL}/users/update-password/`, payload, {
        headers,
      })
      .then((response) => {
        setLoading(false);
        toast.success('Password reset successful');
        window.location.href = '/login'; // Redirect to login page
      })
      .catch((error) => {
        setLoading(false);
        toast.error('Error resetting password');
      });
  };

  return (
    <div className="login-container">
      <div className="content-wrapper content-password">
        <h1> {email} </h1>
        <div className="form-wrapper">
          <form>
            <div
              className={
                showPassword ? 'input-wrapper visible' : 'input-wrapper'
              }
            >
              {simpleValidator.current.message(
                'password',
                formData.password,
                'required',
              )}
              <input
                type={showPassword ? 'text' : 'password'}
                onChange={handleChange}
                value={formData.password}
                name="password"
                placeholder="Password"
              />
              <span className="show-psswd" onClick={onTogglePasswd} />
            </div>
            <div
              className={
                showConfirmPassword ? 'input-wrapper visible' : 'input-wrapper'
              }
            >
              {simpleValidator.current.message(
                'confirm password',
                formData.confirm_password,
                'required|in:' + formData.password,
              )}
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                onChange={handleChange}
                value={formData.confirm_password}
                name="confirm_password"
                placeholder="Confirm password"
              />
              <span className="show-psswd" onClick={onToggleConfirmPasswd} />
            </div>
            <div className="cta-wrapper">
              <button
                className={`submit ${loading ? 'loading' : ''}`}
                type="submit"
                onClick={handleSubmit}
              >
                Reset Password
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { Login };
